import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import {
	CreatorOptions,
	CreatorRendererOptions,
	CreatorState,
	SavedCreatorState,
} from '@/types/creator';
import { RENDERER_CONFIG } from '@/configs/rendererConfig';
import { isDev } from '@/components/App';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { ThreeEvent, ThreeEvents } from '@/renderer/types';
import { ProductModuleCollection } from '@/api/ProductModuleCollectionApi';
import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import { LoadedCreatorData, Project } from '@/api/ProjectApi';
import { UUID } from '@/types/types';
import { roundToHalf } from '@/utils/utilities';
import {
	groupAttributeValues,
	pruneCreatorAttributeSelections,
} from '@/utils/creatorUtils';
import { AttributeValue } from '@/api/AttributeValueApi';
import { current } from 'immer';
import { Preset, PresetStatus } from '@/api/PresetApi';
import { ProductModule } from '@/api/ProductModuleApi';
import APP_CONFIG from '@/configs/appConfig';

const renderer = ProjectRenderer.getInstance();

const url = new URL(window.location.href);
const searchParams = new URLSearchParams(url.search);
export const storeId = searchParams.get('storeId') as UUID;

const initialState: CreatorState = {
	storeId,
	project: {
		id: '' as UUID,
		price: 0,
		creatorProductModules: [],
		createdAt: '',
		updatedAt: '',
	},
	preset: {
		id: '' as UUID,
		name: '',
		price: 0,
		listOrder: 0,
		creatorProductModules: [],
		status: PresetStatus.INACTIVE,
		createdAt: '',
		updatedAt: '',
		options: {},
		storeConfigurations: [],
	},
	selectedPreset: null,
	selectedProductModuleCollection: null,
	attributesWithValues: [],
	globalSelectedAttributeValues: {},
	renderer: {
		cameraDistance: RENDERER_CONFIG.INITIAL_ZOOM,
		loading: false,
	},
	options: {
		isPreviewMode: false,
		isPresetMode: false,
		isOpen: isDev,
		lockedPresetId: null,
		lockedCollectionId: null,
		syncProjectCounter: 0,
		isLoading: false,
		isMetersVisible: false,
		applyValueToAll: false,
		selectedSlot: null,
		selectedCreatorProductModule: null,
		swapDetails: {
			creatorProductModule: null,
			slotTypes: [],
			modulesIds: [],
		},
		summaryDrawerOpened: false,
		downloadDrawerOpened: false,
		helpDrawerOpened: false,
		helpDrawerData: null,
		instructionModalOpened: false,
		moduleContextMenuData: {
			moduleId: null,
			position: [0, 0],
			deleteable: false,
			swapDetails: {
				slotTypes: [],
				modulesIds: [],
			},
		},
	},
	snapshots: [],
};

function withSnapshot(
	state: CreatorState,
	updater: (state: CreatorState) => void
) {
	const modules = state.options.isPresetMode
		? state.preset.creatorProductModules
		: state.project.creatorProductModules;

	if (!modules.length) {
		updater(state);
		return;
	}

	state.snapshots.push({
		modules: current(modules),
		attributesWithValues: current(state.attributesWithValues),
		selectedProductModuleCollection: current(
			state.selectedProductModuleCollection
		),
		globalSelectedAttributeValues: current(state.globalSelectedAttributeValues),
	});
	if (state.snapshots.length > APP_CONFIG.HISTORY_MAX_LENGTH)
		state.snapshots.shift();

	updater(state);
}

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState>>
		) => {
			Object.assign(state, payload);
		},
		createFromPreset: (
			state,
			{ payload }: PayloadAction<{ project: Project; preset: Preset }>
		) => {
			state.selectedPreset = payload.preset;

			state.project.creatorProductModules =
				payload.project.creatorProductModules;
			state.project.price = payload.project.price;

			let attributeId: UUID;
			for (attributeId in state.globalSelectedAttributeValues) {
				state.globalSelectedAttributeValues[attributeId] = null;
			}

			for (const attributeValueId of state.project.creatorProductModules[0]
				.selectedAttributeValuesIds) {
				const shouldSelectGlobal = state.project.creatorProductModules.every(
					(mod) => mod.selectedAttributeValuesIds.includes(attributeValueId)
				);
				if (shouldSelectGlobal) {
					const attributeValue = state.attributesWithValues
						.flatMap((attr) => attr.values)
						.find((val) => val.id === attributeValueId);
					if (!attributeValue) continue;

					state.globalSelectedAttributeValues[attributeValue.attribute.id] =
						attributeValue;
				}
			}
		},
		loadProject: (state, { payload }: PayloadAction<SavedCreatorState>) => {
			state.project = payload.project;
			state.selectedProductModuleCollection =
				payload.selectedProductModuleCollection;
			state.selectedPreset = payload.selectedPreset;
			state.attributesWithValues = payload.attributesWithValues;
			state.globalSelectedAttributeValues =
				payload.globalSelectedAttributeValues;

			if (payload.selectedPreset)
				state.options.lockedPresetId = payload.selectedPreset.id;

			pruneCreatorAttributeSelections(state);
		},
		enterPreviewMode: (
			state,
			{ payload }: PayloadAction<LoadedCreatorData>
		) => {
			state.project = payload.project;
			state.selectedProductModuleCollection = payload.productModuleCollection;

			state.selectedPreset = payload.preset;
			state.attributesWithValues = groupAttributeValues(
				payload.productModuleCollection.attributeValues
			);

			state.globalSelectedAttributeValues = {};

			let attributeId: UUID;
			for (attributeId in payload.globalSelectedAttributeValues) {
				const attributeValueId =
					payload.globalSelectedAttributeValues[attributeId];

				if (!attributeValueId) {
					state.globalSelectedAttributeValues[attributeId] = null;
					continue;
				}

				const attributeWithValues = state.attributesWithValues.find(
					(attribute) => attribute.id === attributeId
				);
				if (!attributeWithValues) continue;

				const attributeValue = attributeWithValues.values.find(
					(value) => value.id === attributeValueId
				);
				if (!attributeValue) continue;

				state.globalSelectedAttributeValues[attributeId] = attributeValue;
			}
		},
		loadPreset: (state, { payload }: PayloadAction<Preset>) => {
			state.preset = payload;
			state.options.isPresetMode = true;
		},
		updateProject: (state, { payload }: PayloadAction<Partial<Project>>) => {
			Object.assign(state.project, payload);
		},
		updatePreset: (state, { payload }: PayloadAction<Partial<Preset>>) => {
			Object.assign(state.preset, payload);
		},
		syncProject: (state, { payload }: PayloadAction<Project>) => {
			state.project.price = payload.price;
			state.project.updatedAt = payload.updatedAt;

			for (const creatorProductModule of state.project.creatorProductModules) {
				const match = payload.creatorProductModules.find(
					(mod) => mod.id === creatorProductModule.id
				);

				if (!match) continue;

				creatorProductModule.code = match.code;
				creatorProductModule.price = match.price;
			}
		},
		syncPreset: (state, { payload }: PayloadAction<Preset>) => {
			state.preset.price = payload.price;
			state.preset.updatedAt = payload.updatedAt;

			for (const creatorProductModule of state.preset.creatorProductModules) {
				const match = payload.creatorProductModules.find(
					(mod) => mod.id === creatorProductModule.id
				);

				if (!match) continue;
				creatorProductModule.code = match.code;
			}
		},
		selectCollection: (
			state,
			{ payload }: PayloadAction<ProductModuleCollection>
		) => {
			if (state.selectedProductModuleCollection?.id === payload.id) return;

			state.selectedPreset = null;
			state.selectedProductModuleCollection = payload;

			state.globalSelectedAttributeValues =
				payload.defaultAttributeValues.reduce<
					CreatorState['globalSelectedAttributeValues']
				>((acc, curr) => {
					acc[curr.attribute.id] = curr;
					return acc;
				}, {});

			state.attributesWithValues = groupAttributeValues(
				payload.attributeValues
			);
			pruneCreatorAttributeSelections(state);
		},
		addCreatorProductModule: (
			state,
			{ payload }: PayloadAction<CreatorProductModule>
		) => {
			withSnapshot(state, () => {
				if (state.options.isPresetMode)
					state.preset.creatorProductModules.push(payload);
				else state.project.creatorProductModules.push(payload);
			});
		},
		removeCreatorProductModule: (
			state,
			{ payload }: PayloadAction<CreatorProductModule>
		) => {
			withSnapshot(state, () => {
				if (state.options.isPresetMode)
					state.preset.creatorProductModules =
						state.preset.creatorProductModules.filter(
							(mod) => mod.id !== payload.id
						);
				else
					state.project.creatorProductModules =
						state.project.creatorProductModules.filter(
							(mod) => mod.id !== payload.id
						);
				state.options.selectedCreatorProductModule = null;
			});
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		updateRenderer: (
			state,
			{ payload }: PayloadAction<Partial<CreatorRendererOptions>>
		) => {
			Object.assign(state.renderer, payload);
		},
		handleRendererEvent: (
			state,
			{
				payload,
			}: PayloadAction<{
				type: ThreeEvent;
				data: ThreeEvents[ThreeEvent];
			}>
		) => {
			const { type } = payload;

			switch (type) {
				case ThreeEvent.SLOT_SELECTION: {
					const data = payload.data as ThreeEvents[ThreeEvent.SLOT_SELECTION];
					state.options.selectedSlot = data;
					state.options.selectedCreatorProductModule = null;
					break;
				}
				case ThreeEvent.MODULE_SELECTION: {
					const data = payload.data as ThreeEvents[ThreeEvent.MODULE_SELECTION];

					if (!data) {
						state.options.selectedCreatorProductModule = null;
						return;
					}

					const modules = state.options.isPresetMode
						? state.preset.creatorProductModules
						: state.project.creatorProductModules;

					const creatorProductModule = modules.find(
						(mod) => mod.id === data.id
					);

					if (!creatorProductModule) return;

					state.options.selectedCreatorProductModule = {
						...creatorProductModule,
						deleteable: data.deleteable,
					};
					break;
				}
				case ThreeEvent.DESELECT_ALL: {
					state.options.selectedSlot = null;
					state.options.selectedCreatorProductModule = null;
					break;
				}
				case ThreeEvent.UPDATE_CAMERA_DISTANCE: {
					state.renderer.cameraDistance = roundToHalf(payload.data as number);
					break;
				}
				case ThreeEvent.SET_CREATOR_PRODUCT_MODULES_ORDER: {
					const data =
						payload.data as ThreeEvents[ThreeEvent.SET_CREATOR_PRODUCT_MODULES_ORDER];

					data.forEach((id, idx) => {
						const creatorProductModule = state.options.isPresetMode
							? state.preset.creatorProductModules.find((mod) => mod.id === id)
							: state.project.creatorProductModules.find(
									(mod) => mod.id === id
								);
						if (!creatorProductModule) return;

						creatorProductModule.listOrder = idx;
					});
					break;
				}
				case ThreeEvent.CONTEXT_MENU: {
					const data = payload.data as ThreeEvents[ThreeEvent.CONTEXT_MENU];
					Object.assign(state.options.moduleContextMenuData, data);
					break;
				}
				default:
					throw new Error('UNKNOW_EVENT');
			}
		},
		toogleHelpDrawer: (
			state,
			{
				payload,
			}: PayloadAction<
				Pick<CreatorOptions, 'helpDrawerOpened' | 'helpDrawerData'>
			>
		) => {
			Object.assign(state.options, payload);
		},
		toogleInstructionModal: (
			state,
			{
				payload,
			}: PayloadAction<
				Pick<CreatorOptions, 'helpDrawerOpened' | 'helpDrawerData'>
			>
		) => {
			Object.assign(state.options, payload);
		},
		handleCreatorProductModuleConfiguration: (
			state,
			{
				payload,
			}: PayloadAction<{
				creatorProductModule: CreatorProductModule;
				attributeValues: AttributeValue[];
			}>
		) => {
			const modules = state.options.isPresetMode
				? state.preset.creatorProductModules
				: state.project.creatorProductModules;

			const creaProdMod = modules.find(
				(mod) => mod.id === payload.creatorProductModule.id
			);
			if (!creaProdMod) return;

			withSnapshot(state, () => {
				for (const attributeValue of payload.attributeValues) {
					const oldValue = creaProdMod.selectedAttributeValues!.find(
						(attr) => attr.attribute.id === attributeValue.attribute.id
					);

					if (oldValue)
						creaProdMod.selectedAttributeValues =
							creaProdMod.selectedAttributeValues!.filter(
								(val) => val.id !== oldValue.id
							);

					creaProdMod.selectedAttributeValues!.push(attributeValue);
					Object.assign(
						state.options.selectedCreatorProductModule!,
						creaProdMod
					);

					if (
						state.globalSelectedAttributeValues[attributeValue.attribute.id]
							?.id !== attributeValue.id
					)
						state.globalSelectedAttributeValues[attributeValue.attribute.id] =
							null;
				}
				pruneCreatorAttributeSelections(state);
			});
		},
		handleCreatorProductModuleSwap: (
			state,
			{
				payload,
			}: PayloadAction<{
				creatorProductModuleId: UUID;
				productModule: ProductModule;
			}>
		) => {
			const modules = state.options.isPresetMode
				? state.preset.creatorProductModules
				: state.project.creatorProductModules;

			const creaProdMod = modules.find(
				(mod) => mod.id === payload.creatorProductModuleId
			);
			if (!creaProdMod) return;

			state.options.swapDetails = {
				creatorProductModule: null,
				slotTypes: [],
				modulesIds: [],
			};

			withSnapshot(state, () => {
				creaProdMod.productModule = payload.productModule;
			});
			pruneCreatorAttributeSelections(state);
		},
		handleCollectionAttributeValuesChange: (
			state,
			{
				payload,
			}: PayloadAction<
				{
					attributeId: UUID;
					attributeValue: AttributeValue | null;
				}[]
			>
		) => {
			withSnapshot(state, () => {
				for (const change of payload) {
					state.globalSelectedAttributeValues[change.attributeId] =
						change.attributeValue;

					const modules = state.options.isPresetMode
						? state.preset.creatorProductModules
						: state.project.creatorProductModules;

					for (const creaProdMod of modules) {
						if (
							creaProdMod.productModule!.hasValuesOverride &&
							change.attributeValue &&
							!creaProdMod.productModule!.attributeValueOverridesIds.includes(
								change.attributeValue.id
							)
						)
							continue;

						const oldValue = creaProdMod.selectedAttributeValues!.find(
							(attr) => attr.attribute.id === change.attributeId
						);

						if (oldValue)
							creaProdMod.selectedAttributeValues =
								creaProdMod.selectedAttributeValues!.filter(
									(val) => val.id !== oldValue.id
								);

						if (change.attributeValue)
							creaProdMod.selectedAttributeValues!.push(change.attributeValue);

						if (change.attributeValue)
							renderer.handleActions(
								change.attributeValue.actions,
								creaProdMod.id
							);
					}
				}
				pruneCreatorAttributeSelections(state);
			});
		},
		refreshAPIData: (state) => {
			state.options.syncProjectCounter++;
		},
		undo: (state) => {
			const snapshot = state.snapshots.pop();
			if (!snapshot) return;

			if (state.options.isPresetMode)
				state.preset.creatorProductModules = snapshot.modules;
			else state.project.creatorProductModules = snapshot.modules;

			state.selectedProductModuleCollection =
				snapshot.selectedProductModuleCollection;
			state.attributesWithValues = snapshot.attributesWithValues;
			state.globalSelectedAttributeValues =
				snapshot.globalSelectedAttributeValues;

			renderer.undo();
		},
		setCreatorProductModuleToSwap: (
			state,
			{ payload }: PayloadAction<CreatorProductModule>
		) => {
			state.options.swapDetails = {
				creatorProductModule: payload,
				...state.options.moduleContextMenuData.swapDetails,
			};
		},
		closeContextMenu: (state) => {
			state.options.moduleContextMenuData = {
				moduleId: null,
				position: [0, 0],
				deleteable: false,
				swapDetails: {
					slotTypes: [],
					modulesIds: [],
				},
			};
		},
		resetCreator: (state) => {},
	},
});

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;
export default creatorSlice.reducer;
